import { Modal, Form, Input, Select, Switch } from "antd";
import { IUserRecord } from "@/ts/common-types";
import { useEffect } from "react";
import { EditClientUserFormFields } from "@/ts/common-types";

interface EditUserModalProps {
    user: IUserRecord | null;
    visible: boolean;
    onCancel: () => void;
    onSave: (values: Partial<IUserRecord>) => Promise<void>;
    roles: { id: number; name: string }[];
    loading: boolean;
}

const EditUserModal = ({
    user,
    visible,
    onCancel,
    onSave,
    roles,
    loading,
}: EditUserModalProps) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                [EditClientUserFormFields.FirstName]: user.first_name,
                [EditClientUserFormFields.LastName]: user.last_name,
                [EditClientUserFormFields.Email]: user.email,
                [EditClientUserFormFields.Status]: user.status,
                [EditClientUserFormFields.Country]: user.country,
                [EditClientUserFormFields.Timezone]: user.timezone,
                [EditClientUserFormFields.Phone]: user.phone,
                [EditClientUserFormFields.RoleId]: user.access[0].role_id,
            });
        }
    }, [user, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await onSave(values);
            form.resetFields();
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    return (
        <Modal
            title="Edit User"
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={600}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name={EditClientUserFormFields.FirstName}
                    label="First Name"
                    rules={[
                        { required: true, message: "Please input first name!" },
                    ]}
                >
                    <Input className="border" />
                </Form.Item>

                <Form.Item
                    name={EditClientUserFormFields.LastName}
                    label="Last Name"
                >
                    <Input className="border" />
                </Form.Item>

                <Form.Item
                    name={EditClientUserFormFields.Email}
                    label="Email"
                    rules={[
                        { required: true, message: "Please input email!" },
                        {
                            type: "email",
                            message: "Please enter a valid email!",
                        },
                    ]}
                >
                    <Input className="border" />
                </Form.Item>

                <Form.Item
                    name={EditClientUserFormFields.RoleId}
                    label="Role"
                    rules={[{ required: true, message: "Please select role!" }]}
                >
                    <Select>
                        {roles.map((role) => (
                            <Select.Option key={role.id} value={role.id}>
                                {role.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name={EditClientUserFormFields.Status}
                    label="Status"
                    valuePropName="checked"
                >
                    <Switch
                        checkedChildren="Active"
                        unCheckedChildren="Inactive"
                    />
                </Form.Item>

                <Form.Item
                    name={EditClientUserFormFields.Country}
                    label="Country"
                >
                    <Input className="border" />
                </Form.Item>

                <Form.Item
                    name={EditClientUserFormFields.Timezone}
                    label="Timezone"
                >
                    <Select
                        showSearch
                        options={(
                            (Intl as any).supportedValuesOf(
                                "timeZone"
                            ) as string[]
                        ).map((tz: string) => ({
                            label: tz,
                            value: tz,
                        }))}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EditUserModal;
